import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import VoiceCard from "@/components/VoiceCard";
import { Sparkles, ArrowRight, Gauge, Languages, Zap, Loader2 } from "lucide-react";

const LANGS = ["English","Dutch","German","French","Spanish","Italian","Japanese","Chinese","Korean","Portuguese","Hindi","Turkish","Russian","Arabic","Swedish","Danish","Norwegian","Polish","Greek","Romanian","Vietnamese","Indonesian","Hungarian","Czech","Finnish","Croatian","Ukrainian","Tamil","Bulgarian","Slovenian"];

export default function AIVoices() {
  const [ai, setAi] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/voices", { params: { voice_type: "ai" } }).then((r) => setAi(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO title="AI Child Voices — Premium AI Kids Voice Generator" path="/ai-voices"
        description="30 exclusive AI child voices in 30+ languages with pitch, speed and emotion control. Generate natural young voices instantly." />
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <div className="container-x relative py-16 md:py-20">
          <span className="label-eyebrow inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5"><Sparkles size={13} /> VoiceOverKids AI</span>
          <h1 className="mt-5 font-heading text-4xl font-black tracking-tighter md:text-6xl">AI child voices</h1>
          <p className="mt-4 max-w-2xl text-lg text-zinc-300">Generate natural-sounding AI child voices in seconds. 30 exclusive voices in 30+ languages — perfect for e-learning, animation, apps and audiobooks. The AI generator runs on our dedicated platform.</p>
          <a href="https://app.voiceoverkids.agency/main" target="_blank" rel="noreferrer" data-testid="ai-cta"
            onClick={() => window.gtag && window.gtag("event", "ai_cta_clicked")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-4 font-bold shadow-lg shadow-violet-500/30 transition hover:bg-brand-violetHover">
            Try AI voices instantly <ArrowRight size={18} />
          </a>
          <p className="mt-3 text-sm text-zinc-400">No subscription, no credit card needed · 1 free token at signup</p>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="grid gap-6 md:grid-cols-3">
          {[[Sparkles, "Exclusive voices", "30 unique kids' voices developed in-house — not found anywhere else."],
            [Gauge, "Full control", "Fine-tune pitch, speed and emotion with Version 2.0."],
            [Languages, "30+ languages", "Paste your script — the generator detects the language automatically."]].map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-zinc-200 bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100 text-brand-violet"><Icon size={20} /></span>
              <h3 className="mt-4 font-heading text-lg font-bold">{t}</h3>
              <p className="mt-1 text-sm text-zinc-500">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white section-y">
        <div className="container-x">
          <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <strong>Note:</strong> These are AI-generated voices, not recordings of real children.
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">AI voice samples</h2>
          <p className="mt-2 mb-8 text-zinc-600">Listen to a selection of the AI child voices available in the generator.</p>
          {loading ? <div className="flex justify-center py-20 text-brand-violet"><Loader2 className="animate-spin" size={28} /></div>
            : <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{ai.map((t) => <VoiceCard key={t.id} talent={t} />)}</div>}
        </div>
      </section>

      <section className="container-x section-y">
        <h2 className="font-heading text-2xl font-bold">Supported languages</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {LANGS.map((l) => <span key={l} className="rounded-full bg-zinc-100 px-4 py-1.5 text-sm font-medium text-zinc-700">{l}</span>)}
        </div>
      </section>
    </>
  );
}
